import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTheme } from 'styled-components';
import { Mail, Lock, Eye, EyeOff, Globe, CheckCircle, ArrowLeft } from 'react-feather';
import logo from '../../../assets/logo.png';
import GradientButton from '../common/GradientButton';
import {
    LoginPageContainer, DesktopPanel, PanelHeading, PanelDescription,
    DesktopLogoImage, TrustBadgesRow, TrustBadge, TrustBadgeIcon,
    TrustBadgeTitle, TrustBadgeDesc, RightPanel, FormCard, MobileHeader,
    MobileLogo, FormTitle, FormSubtitle, FormContainer, InputGroup,
    InputLabel, InputWrapper, InputIcon, StyledInput, ButtonWrapper,
    ButtonContent, FooterRow, FooterAction, LegalFooter,
} from './LoginScreen.styles';

const registerSchema = yup.object({
    email: yup.string().email('Invalid email address').required('Email is required'),
    password: yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Must contain at least one number')
        .required('Password is required'),
    confirmPassword: yup.string()
        .oneOf([yup.ref('password')], 'Passwords do not match')
        .required('Please confirm your password'),
});

const StrengthBar = ({ password }) => {
    const score = !password ? 0
        : [/.{8,}/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/]
            .filter(r => r.test(password)).length;

    const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e'];
    const labels = ['Weak', 'Fair', 'Good', 'Strong'];

    if (!password) return null;

    return (
        <div style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{
                        flex: 1, height: '3px', borderRadius: '2px',
                        backgroundColor: i <= score ? colors[score - 1] : 'rgba(255,255,255,0.1)',
                        transition: 'background-color 0.2s',
                    }} />
                ))}
            </div>
            <span style={{ fontSize: '11px', color: score > 0 ? colors[score - 1] : '#6b7280' }}>
                {score > 0 ? labels[score - 1] : ''}
            </span>
        </div>
    );
};

const RegisterScreen = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const accountType = location.state?.accountType || 'business';

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const {
        control, handleSubmit, watch,
        formState: { errors, isValid }
    } = useForm({
        resolver: yupResolver(registerSchema),
        mode: 'onChange',
        defaultValues: { email: '', password: '', confirmPassword: '' }
    });

    const passwordValue = watch('password');

    const onSubmit = (data) => {
        navigate('/verify-email', { state: { accountType, email: data.email } });
    };

    const eyeStyle = {
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#9ca3af', padding: '0 4px', display: 'flex', alignItems: 'center',
    };

    return (
        <LoginPageContainer>
            <DesktopPanel>
                <DesktopLogoImage src={logo} alt="NYBANQ" />
                <PanelHeading>Banking{'\n'}Without{'\n'}Borders</PanelHeading>
                <PanelDescription>
                    Multi-currency accounts, global transfers, and stablecoins — all in one secure platform.
                </PanelDescription>
                <TrustBadgesRow>
                    <TrustBadge>
                        <TrustBadgeIcon><CheckCircle size={20} color="currentColor" /></TrustBadgeIcon>
                        <TrustBadgeTitle>Bank-Grade Security</TrustBadgeTitle>
                        <TrustBadgeDesc>Enterprise-level compliance.</TrustBadgeDesc>
                    </TrustBadge>
                    <TrustBadge>
                        <TrustBadgeIcon><Globe size={20} color="currentColor" /></TrustBadgeIcon>
                        <TrustBadgeTitle>Multi-Currency</TrustBadgeTitle>
                        <TrustBadgeDesc>USD, EUR.</TrustBadgeDesc>
                    </TrustBadge>
                </TrustBadgesRow>
            </DesktopPanel>

            <RightPanel>
                <FormCard>
                    <MobileHeader>
                        <MobileLogo src={logo} alt="NYBANQ Logo" />
                    </MobileHeader>

                    <FormTitle>Create your account</FormTitle>
                    <FormSubtitle>You'll use these credentials to log in</FormSubtitle>

                    <FormContainer onSubmit={handleSubmit(onSubmit)}>
                        <InputGroup>
                            <InputLabel htmlFor="reg-email">Email Address</InputLabel>
                            <Controller name="email" control={control} render={({ field }) => (
                                <>
                                    <InputWrapper style={{ borderColor: errors.email ? theme.colors.danger?.main || '#ef4444' : undefined }}>
                                        <InputIcon><Mail size={18} /></InputIcon>
                                        <StyledInput {...field} id="reg-email" type="email" placeholder="you@company.com" autoComplete="email" />
                                    </InputWrapper>
                                    {errors.email && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.email.message}</span>}
                                </>
                            )} />
                        </InputGroup>

                        <InputGroup>
                            <InputLabel htmlFor="reg-password">Password</InputLabel>
                            <Controller name="password" control={control} render={({ field }) => (
                                <>
                                    <InputWrapper style={{ borderColor: errors.password ? '#ef4444' : undefined }}>
                                        <InputIcon><Lock size={18} /></InputIcon>
                                        <StyledInput {...field} id="reg-password" type={showPassword ? 'text' : 'password'} placeholder="Min. 8 characters" autoComplete="new-password" />
                                        <button type="button" style={eyeStyle} onClick={() => setShowPassword(p => !p)}>
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </InputWrapper>
                                    <StrengthBar password={passwordValue} />
                                    {errors.password && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.password.message}</span>}
                                </>
                            )} />
                        </InputGroup>

                        <InputGroup>
                            <InputLabel htmlFor="reg-confirm">Confirm Password</InputLabel>
                            <Controller name="confirmPassword" control={control} render={({ field }) => (
                                <>
                                    <InputWrapper style={{ borderColor: errors.confirmPassword ? '#ef4444' : undefined }}>
                                        <InputIcon><Lock size={18} /></InputIcon>
                                        <StyledInput {...field} id="reg-confirm" type={showConfirm ? 'text' : 'password'} placeholder="Repeat your password" autoComplete="new-password" />
                                        <button type="button" style={eyeStyle} onClick={() => setShowConfirm(p => !p)}>
                                            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </InputWrapper>
                                    {errors.confirmPassword && <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.confirmPassword.message}</span>}
                                </>
                            )} />
                        </InputGroup>

                        <ButtonWrapper>
                            <GradientButton
                                colors={['#333333', theme.colors.text?.dark || '#111827']}
                                onPress={handleSubmit(onSubmit)}
                                disabled={!isValid}
                                style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '15px', borderRadius: '10px' }}
                            >
                                <ButtonContent>
                                    <Mail size={16} />
                                    Continue
                                </ButtonContent>
                            </GradientButton>
                        </ButtonWrapper>
                    </FormContainer>

                    <FooterRow>
                        <FooterAction onClick={() => navigate(-1)}>
                            <ArrowLeft size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                            Back
                        </FooterAction>
                        <FooterAction onClick={() => navigate('/login')}>Already have an account?</FooterAction>
                    </FooterRow>
                </FormCard>

                <LegalFooter>
                    <span>© 2026 NYBANQ. All rights reserved.</span>
                    <a href="/privacy.html">Privacy Policy</a>
                    <a href="/terms.html">Terms & Conditions</a>
                </LegalFooter>
            </RightPanel>
        </LoginPageContainer>
    );
};

export default RegisterScreen;
